import { useMemo } from 'react';

import get from 'lodash/get';
import { useLocation } from 'react-router-dom';

import {
  MyCheckApp,
  useAuthStore,
  useCheckoutStore,
  useErrorTrackerHandler,
  useLocationStore,
} from 'mycheck-core';
import {
  CartApiError,
  MenuItem as MenuItemGenericType,
} from 'types/GenericTypes';
import useOutOfStockErrorCallback from 'core/hooks/useOutOfStockErrorCallback';

export function useProductClick() {
  const AuthStore = useAuthStore();
  const LocationStore = useLocationStore();
  const CheckoutStore = useCheckoutStore();

  const { accessToken } = AuthStore;
  const experienceId = LocationStore.selectedExperienceId;
  const businessId = LocationStore.selectedRestaurantId;
  const location = useLocation();
  const { onError } = useErrorTrackerHandler();
  const { outOfStockErrorCallback } = useOutOfStockErrorCallback();

  const config = useMemo(() => {
    const _config = MyCheckApp.instance.getGlobalConfig();

    const showEditItemConfiguration:
      | 'ALWAYS'
      | 'MODIFIERS'
      | 'REQUIRED_MODIFIERS' = get(_config, 'settings.showEditItem', 'ALWAYS');

    return {
      showEditItemConfiguration,
    };
  }, [location.search]);

  const onClickItem = async (
    item: MenuItemGenericType,
    callback: (id: number) => void,
  ) => {
    if (config.showEditItemConfiguration === 'ALWAYS') {
      return callback(item.id);
    }

    if (
      config.showEditItemConfiguration === 'MODIFIERS' &&
      item.modifierGroups.length > 0
    ) {
      return callback(item.id);
    }

    if (
      config.showEditItemConfiguration === 'REQUIRED_MODIFIERS' &&
      item.modifierGroups.filter((e) => e.minSelect > 0).length > 0
    ) {
      return callback(item.id);
    }

    CheckoutStore.setOrderItem({ ...item, quantity: 1, comment: '' });
    try {
      await CheckoutStore.updateOrCreateCheckout({
        accessToken,
        experienceId,
        businessId,
      });
    } catch (error) {
      if (error instanceof CartApiError) {
        onError(error, () =>
          outOfStockErrorCallback(error as CartApiError, false),
        );
      }
    }
  };

  return { onClickItem };
}
